import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ColumnType } from "antd/es/table";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { onSortAntd } from "components/atoms/table/AntdTable";

export type QueryParamValue = string | number | null | undefined;
export type QueryParamPartial = Partial<BasePagination> &
  Record<string, QueryParamValue>;

const shouldDeleteQueryParam = (value: QueryParamValue) =>
  value === null || value === undefined || value === "";

const shouldResetToDefaultPage = (
  key: string,
  value: QueryParamValue,
  prev: URLSearchParams
) => key === "pageNum" && prev.get(key) === null && Number(value) === 0;

export default function useTableQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateQueryInfo = useCallback(
    (partial: QueryParamPartial) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        Object.entries(partial).forEach(([key, value]) => {
          if (
            shouldResetToDefaultPage(key, value, prev) ||
            shouldDeleteQueryParam(value)
          ) {
            next.delete(key);
            return;
          }

          next.set(key, String(value));
        });

        return next;
      });
    },
    [setSearchParams]
  );

  const onClickSearch = useCallback(
    (keyword: string) => {
      updateQueryInfo({
        keyword,
        pageNum: 0,
      });
    },
    [updateQueryInfo]
  );

  const onHeaderCell = useCallback(
    (column: ColumnType<any>) => ({
      onClick: () => {
        if (!column.key) return;

        updateQueryInfo({
          sortType: column.key as string,
          sortDirection: onSortAntd(
            (searchParams.get("sortDirection") as SORT_DIRECTION) ??
              SORT_DIRECTION.ASC
          ),
          pageNum: 0,
        });
      },
    }),
    [searchParams, updateQueryInfo]
  );

  const onChangePage = useCallback(
    (page: number) => {
      updateQueryInfo({ pageNum: page - 1 });
    },
    [updateQueryInfo]
  );

  return {
    searchParams,
    setSearchParams,
    updateQueryInfo,
    onClickSearch,
    onHeaderCell,
    onChangePage,
  };
}
